import { useContext } from 'react';
import { createPortal } from 'react-dom';
import CardHeaderSlotContext from '../../StateManagment/CardHeaderSlot';
import LiveReportRenderContext from '../../StateManagment/LiveReportRenderContext';
import InlineDetailsContext from '../../StateManagment/InlineDetailsContext';
import DeepAnalysisSlotsContext from '../../StateManagment/DeepAnalysisSlotsContext';
import InfoPopover from './InfoPopover';

/**
 * Renders an InfoPopover into the card bottom bar's info slot (right of the
 * drag handle) when a card frame is available, otherwise inline at the call
 * site (impact overview — contexts without card chrome).
 *
 * Inside a live report the info icon is suppressed entirely (returns null) so
 * reports stay clean. On a deep-analysis page the content is shown on the page
 * instead of behind the icon: in the page's details slot (its own card above
 * the results) when there is one, otherwise inline as a block. This is the single choke point every
 * visualisation's info popover flows through.
 *
 * @param {object} props
 * @param {React.ReactNode} props.content - Popover body.
 * @param {string} [props.label] - Tooltip text and modal header.
 */
const CardHeaderInfoPopover = ({ content, label = 'Details' }) => {
    const slots = useContext(CardHeaderSlotContext);
    const inLiveReport = useContext(LiveReportRenderContext);
    const inlineDetails = useContext(InlineDetailsContext);
    const deepAnalysisSlots = useContext(DeepAnalysisSlotsContext);

    if (inLiveReport) return null;

    // Deep-analysis page: surface the detail content directly on the page.
    if (inlineDetails) {
        const details = (
            <div className="deep-analysis-inline-details">
                <h4 className="deep-analysis-inline-details-label">{label}</h4>
                {content}
            </div>
        );
        const detailsEl = deepAnalysisSlots?.detailsEl;
        return detailsEl ? createPortal(details, detailsEl) : details;
    }

    const popover = <InfoPopover label={label} content={content} />;

    return slots?.infoSlot ? createPortal(popover, slots.infoSlot) : popover;
};

export default CardHeaderInfoPopover;
